import {
    Box,
    Text,
    Flex,
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";
import { useAppToast } from "../../../../hooks/useAppToast";
import DiscountCodesService from "../../../../services/discountCodes/discountCodesService";
import DiscountCodeModal from "../../../../shared/components/DiscountCode/DiscountCodeModal";

const DiscountCodesPanel = ({ eventId, ticketTypes, isEditing }) => {
    const { t } = useTranslation("default");
    const toast = useAppToast();

    const [discountCodes, setDiscountCodes] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDiscountCode, setEditingDiscountCode] = useState(null);
    const [discountCodeToDelete, setDiscountCodeToDelete] = useState(null);
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const cancelRef = useRef();

    const fetchDiscountCodes = async () => {
        try {
            const response = await DiscountCodesService.getDiscountCodes(eventId);
            setDiscountCodes(response.data.data ?? response.data ?? []);
        } catch {
            toast({ title: t('discountCode.error.fetch'), status: 'error', duration: 3000 });
        }
    };

    useEffect(() => {
        if (eventId) fetchDiscountCodes();
    }, [eventId]);

    const formatValue = (discountCode) =>
        discountCode.type === 'Percentage' ? `${discountCode.value}%` : `${discountCode.value} zł`;

    const formatScope = (discountCode) =>
        discountCode.scope === 'SpecificTicketType'
            ? (discountCode.eventTicketTypeName ?? t('discountCode.scope_specific_ticket'))
            : t('discountCode.scope_whole_order');

    const formatUsage = (discountCode) =>
        discountCode.maxUsageCount != null
            ? `${discountCode.usageCount || 0} / ${discountCode.maxUsageCount}`
            : `${discountCode.usageCount || 0}`;

    const formatValidity = (discountCode) => {
        if (!discountCode.validFrom && !discountCode.validUntil) return t('discountCode.panel.no_expiry');
        const from = discountCode.validFrom ? dayjs(discountCode.validFrom).format('DD.MM.YYYY') : t('discountCode.panel.no_start_date');
        const until = discountCode.validUntil ? dayjs(discountCode.validUntil).format('DD.MM.YYYY') : t('discountCode.panel.no_end_date');
        return `${from} — ${until}`;
    };

    const handleSave = async (data) => {
        try {
            if (data.id) {
                await DiscountCodesService.updateDiscountCode(eventId, data.id, data);
                toast({ title: t('discountCode.success.updated'), status: 'success', duration: 3000 });
            } else {
                await DiscountCodesService.createDiscountCode(eventId, data);
                toast({ title: t('discountCode.success.created'), status: 'success', duration: 3000 });
            }
            setIsModalOpen(false);
            await fetchDiscountCodes();
        } catch (error) {
            toast({
                title: error?.response?.data?.message ?? t('discountCode.error.save'),
                status: 'error',
                duration: 3000
            });
        }
    };

    const handleDeleteClick = (discountCode) => {
        setDiscountCodeToDelete(discountCode);
        onDeleteOpen();
    };

    const handleDeleteConfirm = async () => {
        if (!discountCodeToDelete) return;
        try {
            await DiscountCodesService.deleteDiscountCode(eventId, discountCodeToDelete.id);
            toast({ title: t('discountCode.success.deleted'), status: 'success', duration: 3000 });
            await fetchDiscountCodes();
        } catch {
            toast({ title: t('discountCode.error.delete'), status: 'error', duration: 3000 });
        } finally {
            setDiscountCodeToDelete(null);
            onDeleteClose();
        }
    };

    return (
        <Box w="100%" bg="white" borderRadius={'12px'}>
            <Text className="title">{t('discountCode.panel.title')}</Text>
            <Text className="subtitle">{t('discountCode.panel.info')}</Text>

            <Box mb={4} mt={4}>
                {discountCodes.length > 0 ? (
                    <Box w="100%" borderRadius={'12px'} overflow="hidden">
                        <Box display={{ base: 'none', lg: 'flex' }} px={4} py={2}>
                            <Box flex="2">{t('discountCode.panel.th.code')}</Box>
                            <Box flex="1">{t('discountCode.panel.th.value')}</Box>
                            <Box flex="1">{t('discountCode.panel.th.type')}</Box>
                            <Box flex="2">{t('discountCode.panel.th.scope')}</Box>
                            <Box flex="1" textAlign="center">{t('discountCode.panel.th.usage')}</Box>
                            <Box flex="2">{t('discountCode.panel.th.validity')}</Box>
                            <Box flex="1" textAlign="center">{t('discountCode.panel.th.active')}</Box>
                            <Box flex="0.5"></Box>
                        </Box>
                        <Flex direction="column" gap={{ base: 4, lg: 0 }}>
                            {discountCodes.map((discountCode) => (
                                <Box key={discountCode.id} display="flex" flexDirection={{ base: 'column', lg: 'row' }} bg="#F8F5F4" fontWeight="bold" alignItems={{ base: 'flex-start', lg: 'center' }} px={4} py={{ base: 4, lg: 2 }} borderRadius={{ base: 4, lg: 0 }}>
                                    <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.code')}:</Text>
                                        <Text as="span">{discountCode.code}</Text>
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.value')}:</Text>
                                        <Text as="span">{formatValue(discountCode)}</Text>
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.type')}:</Text>
                                        <Text as="span">{discountCode.type === 'Percentage' ? t('discountCode.form.type_percentage') : t('discountCode.form.type_amount')}</Text>
                                    </Box>
                                    <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.scope')}:</Text>
                                        <Text as="span" textAlign={{ base: "right", lg: "left" }}>{formatScope(discountCode)}</Text>
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'center' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.usage')}:</Text>
                                        <Text as="span" textAlign={{ base: "right", lg: "center" }}>{formatUsage(discountCode)}</Text>
                                    </Box>
                                    <Box flex="2" display="flex" justifyContent={{ base: 'space-between', lg: 'flex-start' }} w="100%" mb={{ base: 2, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.validity')}:</Text>
                                        <Text as="span" textAlign={{ base: "right", lg: "left" }}>{formatValidity(discountCode)}</Text>
                                    </Box>
                                    <Box flex="1" display="flex" justifyContent={{ base: 'space-between', lg: 'center' }} w="100%" mb={{ base: 4, lg: 0 }}>
                                        <Text display={{ base: 'block', lg: 'none' }} fontWeight="normal" color="gray.600" as="span">{t('discountCode.panel.th.active')}:</Text>
                                        <Text as="span" textAlign={{ base: "right", lg: "center" }}>
                                            {discountCode.isActive ? t('discountCode.panel.active_yes') : t('discountCode.panel.active_no')}
                                        </Text>
                                    </Box>
                                    <Box flex="0.5" display="flex" gap={2} justifyContent={{ base: 'flex-start', lg: 'flex-end' }} w="100%">
                                        <Button size="sm" isDisabled={!isEditing} onClick={() => handleDeleteClick(discountCode)}>
                                            <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M14.125 4.55556L13.3664 15.3484C13.3349 15.797 13.1374 16.2167 12.8135 16.5232C12.4896 16.8296 12.0634 17 11.6208 17H4.37925C3.93662 17 3.51043 16.8296 3.18652 16.5232C2.86262 16.2167 2.66505 15.797 2.63363 15.3484L1.875 4.55556M6.25 8.11111V13.4444M9.75 8.11111V13.4444M10.625 4.55556V1.88889C10.625 1.65314 10.5328 1.42705 10.3687 1.26035C10.2046 1.09365 9.98206 1 9.75 1H6.25C6.01794 1 5.79538 1.09365 5.63128 1.26035C5.46719 1.42705 5.375 1.65314 5.375 1.88889V4.55556M1 4.55556H15" stroke="#7A1F35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </Button>
                                        <Button size="sm" isDisabled={!isEditing} onClick={() => {
                                            setEditingDiscountCode(discountCode);
                                            setIsModalOpen(true);
                                        }}>
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M17.414 2.58594C17.0389 2.211 16.5303 2.00037 16 2.00037C15.4697 2.00037 14.9611 2.211 14.586 2.58594L7 10.1719V12.9999H9.828L17.414 5.41394C17.7889 5.03888 17.9996 4.53027 17.9996 3.99994C17.9996 3.46961 17.7889 2.96099 17.414 2.58594Z" fill="#7A1F35" />
                                                <path fillRule="evenodd" clipRule="evenodd" d="M2 6C2 5.46957 2.21071 4.96086 2.58579 4.58579C2.96086 4.21071 3.46957 4 4 4H8C8.26522 4 8.51957 4.10536 8.70711 4.29289C8.89464 4.48043 9 4.73478 9 5C9 5.26522 8.89464 5.51957 8.70711 5.70711C8.51957 5.89464 8.26522 6 8 6H4V16H14V12C14 11.7348 14.1054 11.4804 14.2929 11.2929C14.4804 11.1054 14.7348 11 15 11C15.2652 11 15.5196 11.1054 15.7071 11.2929C15.8946 11.4804 16 11.7348 16 12V16C16 16.5304 15.7893 17.0391 15.4142 17.4142C15.0391 17.7893 14.5304 18 14 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V6Z" fill="#7A1F35" />
                                            </svg>
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Flex>
                    </Box>
                ) : (
                    <Text mt={2} mb={2}>{t('discountCode.panel.not_found')}</Text>
                )}

                <Flex justify="center" gap={4} mt={6}>
                    <Button
                        onClick={() => {
                            setEditingDiscountCode(null);
                            setIsModalOpen(true);
                        }}
                        isDisabled={!isEditing}
                        bg="brand.500" color="white" borderRadius={'12px'} _hover={{ bg: 'brand.600' }} px="48px" py="25px">
                        <Text mr={'10px'}>{t('discountCode.panel.add')}</Text>
                        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.5 5.66667V8M8.5 8V10.3333M8.5 8H10.8333M8.5 8H6.16667M15.5 8C15.5 8.91925 15.3189 9.82951 14.9672 10.6788C14.6154 11.5281 14.0998 12.2997 13.4497 12.9497C12.7997 13.5998 12.0281 14.1154 11.1788 14.4672C10.3295 14.8189 9.41925 15 8.5 15C7.58075 15 6.6705 14.8189 5.82122 14.4672C4.97194 14.1154 4.20026 13.5998 3.55025 12.9497C2.90024 12.2997 2.38463 11.5281 2.03284 10.6788C1.68106 9.82951 1.5 8.91925 1.5 8C1.5 6.14348 2.2375 4.36301 3.55025 3.05025C4.86301 1.7375 6.64348 1 8.5 1C10.3565 1 12.137 1.7375 13.4497 3.05025C14.7625 4.36301 15.5 6.14348 15.5 8Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Button>
                </Flex>
            </Box>

            <DiscountCodeModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSave}
                initialData={editingDiscountCode}
                ticketTypes={ticketTypes}
            />

            <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            {t('discountCode.delete.modal_title')}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {t('discountCode.delete.modal_confirmation')} "{discountCodeToDelete?.code}"?
                        </AlertDialogBody>
                        <AlertDialogFooter gap="8px">
                            <Button ref={cancelRef} onClick={onDeleteClose}>{t('action.cancel')}</Button>
                            <Button colorScheme="red" onClick={handleDeleteConfirm}>{t('action.delete')}</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default DiscountCodesPanel;
